import type { HomeThemeId } from "@/home/themes";

/** Thailand wall clock — day band for home lighting. */
export const HOME_DAY_TZ = "Asia/Bangkok";
export const HOME_DAY_START_HOUR = 6;
export const HOME_DAY_END_HOUR = 18;

/** Resolve hour 0–23 in the given IANA zone. */
export function hourInTimeZone(date: Date, timeZone = HOME_DAY_TZ): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "numeric",
    hour12: false,
  }).formatToParts(date);
  const hourPart = parts.find((p) => p.type === "hour")?.value;
  const hour = Number.parseInt(hourPart ?? "", 10);
  if (!Number.isFinite(hour)) return date.getHours();
  return hour === 24 ? 0 : hour;
}

/** 06:00–17:59 → day, else night (Asia/Bangkok by default). */
export function themeIdFromClock(
  date: Date = new Date(),
  timeZone = HOME_DAY_TZ,
): HomeThemeId {
  const hour = hourInTimeZone(date, timeZone);
  return hour >= HOME_DAY_START_HOUR && hour < HOME_DAY_END_HOUR ? "day" : "night";
}

/**
 * Approximate server now via HTTP Date header.
 * Falls back to local clock when the header is missing.
 */
/** Wall clock label in UTC (e.g. `14:32 UTC`). */
function formatUtcClockTime(date: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function formatUtcClockLabel(date: Date = new Date()): string {
  return `${formatUtcClockTime(date)} UTC`;
}

/** HH:mm for compact HUD chips (full label via `title` on the element). */
export function formatUtcClockCompact(date: Date = new Date()): string {
  return formatUtcClockTime(date);
}

export async function fetchServerDate(): Promise<Date> {
  try {
    const res = await fetch(`${window.location.origin}/`, {
      method: "HEAD",
      cache: "no-store",
    });
    const header = res.headers.get("date");
    if (header) {
      const parsed = new Date(header);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
  } catch {
    /* ignore — use local */
  }
  return new Date();
}
